import styled, { keyframes } from 'styled-components'
import { tokens } from '../../styles/tokens'

interface Props {
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
  variant?: 'danger' | 'primary'
}

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(45, 27, 78, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 24px;
  animation: ${fadeIn} 0.15s ease;
  backdrop-filter: blur(2px);
`

const Panel = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 24px 64px rgba(45, 27, 78, 0.2);
  animation: ${slideUp} 0.2s ease;
  overflow: hidden;
`

const IconWrapper = styled.div<{ $variant: 'danger' | 'primary' }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  background: ${p => (p.$variant === 'danger' ? '#fdeef4' : tokens.purple50)};
  color: ${p => (p.$variant === 'danger' ? '#c0436a' : tokens.purple600)};
  font-size: 22px;
`

const Body = styled.div`
  padding: 32px 28px 24px;
  text-align: center;
`

const Title = styled.h3`
  font-family: 'Cormorant Garamond', serif;
  font-size: 22px;
  font-weight: 400;
  color: ${tokens.purple900};
  margin-bottom: 10px;
`

const Message = styled.p`
  font-size: 13px;
  color: ${tokens.purple400};
  line-height: 1.6;
`

const Footer = styled.div`
  padding: 0 28px 28px;
  display: flex;
  gap: 10px;
`

const CancelButton = styled.button`
  flex: 1;
  padding: 11px;
  border-radius: 10px;
  border: 1px solid ${tokens.purple100};
  background: white;
  color: ${tokens.purple400};
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${tokens.purple50};
  }
`

const ConfirmButton = styled.button<{ $variant: 'danger' | 'primary' }>`
  flex: 1;
  padding: 11px;
  border-radius: 10px;
  border: none;
  background: ${p =>
    p.$variant === 'danger'
      ? 'linear-gradient(135deg, #c0436a, #e0607e)'
      : `linear-gradient(135deg, ${tokens.purple600}, ${tokens.purple400})`};
  color: white;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition:
    opacity 0.15s,
    transform 0.15s;
  box-shadow: ${p =>
    p.$variant === 'danger'
      ? '0 4px 12px rgba(192, 67, 106, 0.3)'
      : '0 4px 12px rgba(107, 58, 138, 0.25)'};
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

function DangerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <path
        d="M11 2L20.5 19H1.5L11 2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M11 9v4M11 15.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function InfoIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
      <circle cx="11" cy="11" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 10v5M11 7.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
  loading,
  variant = 'danger',
}: Props) {
  if (!open) return null

  return (
    <Overlay onClick={onCancel}>
      <Panel onClick={e => e.stopPropagation()}>
        <Body>
          <IconWrapper $variant={variant}>
            {variant === 'danger' ? <DangerIcon /> : <InfoIcon />}
          </IconWrapper>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Body>
        <Footer>
          <CancelButton onClick={onCancel} disabled={loading}>
            Cancel
          </CancelButton>
          <ConfirmButton $variant={variant} onClick={onConfirm} disabled={loading}>
            {loading ? `${confirmLabel}…` : confirmLabel}
          </ConfirmButton>
        </Footer>
      </Panel>
    </Overlay>
  )
}
